import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  TextField,
  IconButton,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useTranslation } from "react-i18next";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  addDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import ChatMarkdown from "./ChatMarkdown";
import ImageLightbox from "./ImageLightbox";

const ROLE_STYLES = {
  student: { align: "flex-start", bg: "#eef0f6", label: "" },
  assistant: { align: "flex-start", bg: "#f3e8ff", label: "Darssy AI" },
  teacher: { align: "flex-end", bg: "#dcfce7", label: "" },
};

export default function TeacherExerciseConversations({ groupId, exerciseId }) {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [selectedStudentUid, setSelectedStudentUid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    if (!groupId || !exerciseId) return undefined;

    const conversationsRef = collection(
      db,
      "groups",
      groupId,
      "exercises",
      exerciseId,
      "conversations"
    );

    const unsubscribe = onSnapshot(conversationsRef, (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        if (Boolean(b.escalated) !== Boolean(a.escalated)) {
          return Boolean(b.escalated) - Boolean(a.escalated);
        }
        return (b.lastMessageAt?.toMillis?.() || 0) - (a.lastMessageAt?.toMillis?.() || 0);
      });
      setConversations(list);
    });

    return unsubscribe;
  }, [groupId, exerciseId]);

  useEffect(() => {
    if (!groupId || !exerciseId || !selectedStudentUid) {
      setMessages([]);
      return undefined;
    }

    const messagesRef = query(
      collection(
        db,
        "groups",
        groupId,
        "exercises",
        exerciseId,
        "conversations",
        selectedStudentUid,
        "messages"
      ),
      orderBy("createdAt", "asc")
    );

    const unsubscribe = onSnapshot(messagesRef, (snapshot) => {
      setMessages(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return unsubscribe;
  }, [groupId, exerciseId, selectedStudentUid]);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === selectedStudentUid) || null,
    [conversations, selectedStudentUid]
  );

  const handleSendReply = async () => {
    if (!reply.trim() || !selectedStudentUid) return;

    const conversationRef = doc(
      db,
      "groups",
      groupId,
      "exercises",
      exerciseId,
      "conversations",
      selectedStudentUid
    );

    await addDoc(collection(conversationRef, "messages"), {
      role: "teacher",
      content: reply.trim(),
      createdAt: serverTimestamp(),
    });

    await updateDoc(conversationRef, {
      escalated: false,
      lastMessageAt: serverTimestamp(),
    });

    setReply("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        mt: 2,
        minHeight: { md: 400 },
        direction: "rtl",
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", md: "35%" },
          borderInlineEnd: { md: "1px solid #e5e7eb" },
          borderBottom: { xs: "1px solid #e5e7eb", md: "none" },
          pe: { md: 1 },
          pb: { xs: 1, md: 0 },
          maxHeight: { xs: 240, md: "none" },
          overflowY: { xs: "auto", md: "visible" },
        }}
      >
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          {t("المحادثات ({{count}})", { count: conversations.length })}
        </Typography>
        <List dense>
          {conversations.map((conversation) => (
            <ListItemButton
              key={conversation.id}
              selected={conversation.id === selectedStudentUid}
              onClick={() => setSelectedStudentUid(conversation.id)}
            >
              <ListItemText
                primary={conversation.studentName || t("Student")}
                secondary={conversation.escalated ? t("يحتاج مساعدتك") : t("جارية")}
              />
              {conversation.escalated && (
                <Chip size="small" color="error" label={t("متصعّدة")} />
              )}
            </ListItemButton>
          ))}
          {conversations.length === 0 && (
            <Typography sx={{ color: "#94a3b8", px: 1 }}>
              {t("لا توجد محادثات بعد.")}
            </Typography>
          )}
        </List>
      </Box>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: { xs: 420, md: "auto" } }}>
        {!selectedConversation ? (
          <Typography sx={{ color: "#94a3b8", m: "auto" }}>
            {t("اختر محادثة لعرضها")}
          </Typography>
        ) : (
          <>
            <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1, p: 1 }}>
              {messages.map((message) => {
                const roleStyle = ROLE_STYLES[message.role] || ROLE_STYLES.student;
                return (
                  <Box
                    key={message.id}
                    sx={{
                      alignSelf: roleStyle.align,
                      bgcolor: roleStyle.bg,
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      maxWidth: "75%",
                    }}
                  >
                    {roleStyle.label && (
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#7c3aed" }}>
                        {roleStyle.label}
                      </Typography>
                    )}
                    {(() => {
                      const imageUrls = Array.isArray(message.imageUrls)
                        ? message.imageUrls
                        : message.imageUrl
                        ? [message.imageUrl]
                        : [];
                      return imageUrls.length > 0 ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                          {imageUrls.map((url, i) => (
                            <Box
                              key={i}
                              component="img"
                              src={url}
                              alt=""
                              onClick={() => setLightboxUrl(url)}
                              sx={{
                                maxWidth: imageUrls.length > 1 ? 150 : 220,
                                maxHeight: imageUrls.length > 1 ? 150 : 220,
                                borderRadius: 2,
                                display: "block",
                                cursor: "pointer",
                              }}
                            />
                          ))}
                        </Box>
                      ) : null;
                    })()}
                    <ChatMarkdown content={message.content} />
                    {message.transcript && (
                      <Box
                        dir="auto"
                        sx={{
                          mt: 0.75,
                          pt: 0.75,
                          borderTop: "1px dashed #d1d5db",
                          fontSize: 12,
                          color: "#4b5563",
                        }}
                      >
                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#7c3aed", mb: 0.25 }}>
                          {t("النص كما فهمه Darssy AI:")}
                        </Typography>
                        {message.transcript}
                      </Box>
                    )}
                    {message.policyFlag && message.policyFlag !== "none" && (
                      <Chip
                        size="small"
                        sx={{ mt: 0.5 }}
                        label={
                          message.policyFlag === "direct_answer_attempt"
                            ? t("محاولة الحصول على الجواب مباشرة")
                            : t("محاولة تلاعب بالتعليمات")
                        }
                      />
                    )}
                  </Box>
                );
              })}
            </Box>

            <Divider />

            <Box sx={{ display: "flex", gap: 1, p: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t("اكتب رداً للطالب...")}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendReply()}
              />
              <IconButton color="primary" onClick={handleSendReply}>
                <SendIcon />
              </IconButton>
            </Box>
          </>
        )}
      </Box>

      <ImageLightbox src={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </Box>
  );
}
